import { fetchApi, fetchAuthApi } from '@/api/client';
import type { SiteResponse, ZoneResponse, FloorResponse } from '@/types/site';

/**
 * Phase 4: 사이트 (Site) 는 ontoh-auth-api 가 truth.
 * 구역(Zone) · 층(Floor) 은 cctv 도메인 — cctv-api 가 그대로 보유 (카메라 배치 단위).
 *
 * 경로:
 *   - `/v1/sites/*`                              → auth-api
 *   - `/api/v1/sites/{id}/zones/...`             → cctv-api (URL 무변경)
 *   - `/api/v1/sites/{id}/zones/{zid}/floors/...` → cctv-api
 */

// ──── Site (auth-api) ────

interface SiteListResponse {
  items: SiteResponse[];
  total?: number;
  next_cursor?: string | null;
}

export async function getSites(): Promise<{ items: SiteResponse[]; total: number }> {
  // Phase 5c — service=cctv: 자기 조직 사이트 + 조상 조직이 cctv 위임한 사이트 모두 포함
  const data = await fetchAuthApi<SiteListResponse | SiteResponse[]>('/v1/sites?service=cctv');
  if (Array.isArray(data)) {
    return { items: data, total: data.length };
  }
  return { items: data.items, total: data.total ?? data.items.length };
}

export async function getSite(siteId: string): Promise<SiteResponse> {
  return fetchAuthApi<SiteResponse>(`/v1/sites/${siteId}?service=cctv`);
}

export async function createSite(data: {
  name: string;
  address?: string | null;
  description?: string | null;
}): Promise<SiteResponse> {
  return fetchAuthApi<SiteResponse>('/v1/sites', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSite(
  siteId: string,
  data: {
    name?: string | null;
    address?: string | null;
    description?: string | null;
  },
): Promise<SiteResponse> {
  return fetchAuthApi<SiteResponse>(`/v1/sites/${siteId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSite(siteId: string): Promise<void> {
  await fetchAuthApi(`/v1/sites/${siteId}`, { method: 'DELETE' });
}

// ──── Zone · Floor (cctv-api) ────

export async function getZones(siteId: string): Promise<ZoneResponse[]> {
  return fetchApi<ZoneResponse[]>(`/api/v1/sites/${siteId}/zones`);
}

export async function getFloors(siteId: string, zoneId: string): Promise<FloorResponse[]> {
  return fetchApi<FloorResponse[]>(`/api/v1/sites/${siteId}/zones/${zoneId}/floors`);
}

export async function createZone(
  siteId: string,
  data: { name: string; type: string },
): Promise<ZoneResponse> {
  return fetchApi<ZoneResponse>(`/api/v1/sites/${siteId}/zones`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateZone(
  siteId: string,
  zoneId: string,
  data: { name?: string | null; type?: string | null },
): Promise<ZoneResponse> {
  return fetchApi<ZoneResponse>(`/api/v1/sites/${siteId}/zones/${zoneId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteZone(siteId: string, zoneId: string): Promise<void> {
  await fetchApi(`/api/v1/sites/${siteId}/zones/${zoneId}`, { method: 'DELETE' });
}

export async function createFloor(
  siteId: string,
  zoneId: string,
  data: { name: string; level: number },
): Promise<FloorResponse> {
  return fetchApi<FloorResponse>(`/api/v1/sites/${siteId}/zones/${zoneId}/floors`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFloor(
  siteId: string,
  zoneId: string,
  floorId: string,
  data: { name?: string | null; level?: number | null },
): Promise<FloorResponse> {
  return fetchApi<FloorResponse>(
    `/api/v1/sites/${siteId}/zones/${zoneId}/floors/${floorId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
}

export async function deleteFloor(
  siteId: string,
  zoneId: string,
  floorId: string,
): Promise<void> {
  await fetchApi(`/api/v1/sites/${siteId}/zones/${zoneId}/floors/${floorId}`, {
    method: 'DELETE',
  });
}
